import { useMemo } from 'react';
import type { TaskEvent, TaskMember } from '@shared/types/tasks';
import { disputeComplete, disputeOpen, disputeUnfinished, type DisputeRound } from '@shared/factory-dispute';
import { formatDateTime } from '@/lib/formatters';
import { FactoryDisputeCard, type DisputePick } from './FactoryDisputeCard';
import { FactoryQuestionsCard } from './FactoryQuestionsCard';
import type { RoundUpstream } from './TaskToAnswer';
import { decisionRoundId, decisionRounds, decisionsAnchorId, disputeRoundId, taskDisputeRounds, type DecisionRound } from './task-decisions';
import { formatRelative, memberName } from './task-ui';
import { payloadConcerns } from '@shared/decision-concern';
import { useItemChats } from './use-suggestion-chats';
import { isBuildGrill } from '@shared/factory-questions';

/**
 * T-22: the AI builder's answered questions on a task, under the description, newest round first, each
 * with the answer given, so the task's decisions stay easy to find. T-72: each round starts folded to
 * one line (how many questions, who answered and when) and opens on a click; the rounds still waiting
 * are answered at the top of the task (TaskToAnswer). The comments show a pointer line
 * (FactoryQuestionsPointer) in place of each round. SF-T-74: a run's disputed review, once settled
 * (or answered in a comment, or replaced), sits here too, folded, among the questions by date; while
 * it's still to settle, it's in TaskToAnswer. Nothing shows without answered questions or such reviews.
 */
export function TaskDecisions({
  taskId,
  events,
  members,
}: {
  taskId: string;
  /** The task's events, oldest first. */
  events: TaskEvent[];
  members: TaskMember[];
}) {
  const rounds = useMemo(() => decisionRounds(events).filter((r) => r.answered), [events]);
  const disputes = useMemo(() => taskDisputeRounds(events).filter((d) => !disputeOpen(d) && !disputeUnfinished(d)), [events]);
  if (!rounds.length && !disputes.length) return null;
  // Questions and reviews, newest first.
  const cards = [
    ...rounds.map((round) => ({ at: round.event.created_at, id: round.event.id, round, dispute: null })),
    ...disputes.map((dispute) => ({ at: dispute.message.created_at, id: dispute.message.id, round: null, dispute })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <section id={decisionsAnchorId(taskId)} className="scroll-mt-4 space-y-2" aria-label="Decisions">
      <h3 className="text-sm font-semibold">Decisions</h3>
      <div className="space-y-2">
        {cards.map(({ id, round, dispute }) =>
          dispute ? (
            <DisputeRoundCard key={id} round={dispute} />
          ) : round ? (
            // SF-T-308: an answered round keeps its questions' chats, read only (as a decided suggestion's).
            <DecisionRoundCard key={id} round={round} members={members} chat={{ taskId, canAsk: false }} />
          ) : null
        )}
      </div>
    </section>
  );
}

/** One round of questions: folded once answered, open to answer while waiting (with `onAnswer`). */
export function DecisionRoundCard({
  round,
  members,
  onAnswer,
  upstream,
  chat,
}: {
  round: DecisionRound;
  members: TaskMember[];
  /** SF-T-308: each question's live chat, as a suggestion's (useItemChats). Leave out in a preview. */
  chat?: { taskId: string; project?: string; canAsk: boolean; canStop?: boolean };
  /** Posts the answers as a chat message on the task; leave out where nobody can answer (a preview). */
  onAnswer?: (message: string) => void;
  /** T-58: the task's source, on a mirrored task: sent questions say so, and Send to <source> sends them. */
  upstream?: RoundUpstream;
}) {
  const askSlot = useItemChats(round.event.id, chat);
  return (
    <FactoryQuestionsCard
      id={decisionRoundId(round.event.id)}
      askSlot={askSlot}
      // SF-T-308: a build's grilling can be cut short (not a feedback run's questions, or a conflict fix's).
      canEnough={Boolean(round.event.actor_label?.startsWith('Factory')) && isBuildGrill(round.event.body ?? '')}
      fold={round.answered}
      questions={round.questions}
      concerns={payloadConcerns(round.event.payload)}
      answered={round.answered}
      closed={round.closed}
      answers={round.answers}
      backlog={round.backlog}
      words={round.words}
      onAnswer={onAnswer}
      upstream={
        upstream
          ? {
              sourceLabel: upstream.sourceLabel,
              sent: round.sent,
              proposals: round.proposals,
              // Per item in the card: still waiting on the source, and no reply read for its send yet.
              waiting: upstream.waiting,
              onSend: upstream.onSend ? (numbers, mentionId) => upstream.onSend!(round.event.id, numbers, mentionId) : undefined,
              tagging: upstream.tagging,
            }
          : undefined
      }
      meta={
        <span data-hint={formatDateTime(new Date(round.event.created_at))}>
          {round.event.actor_label ? `${round.event.actor_label} · ` : null}
          {formatRelative(round.event.created_at)}
          {round.reply ? ` · answered by ${memberName(members.find((m) => m.user_id === round.reply?.actor_user_id))}` : null}
        </span>
      }
    />
  );
}

/**
 * SF-T-74: one disputed review: folded once settled (or answered in a comment, or replaced), open to
 * settle while it's waiting or didn't finish (with `onSettle`).
 */
export function DisputeRoundCard({
  round,
  onSettle,
  onPicksChange,
}: {
  round: DisputeRound<TaskEvent>;
  /** Settles the findings still open (settleTaskDispute); resolves true when it worked. Leave out where nobody can. */
  onSettle?: (eventId: string, picks: DisputePick[]) => Promise<boolean>;
  onPicksChange?: (count: number) => void;
}) {
  const waiting = disputeOpen(round) || disputeUnfinished(round);
  return (
    <FactoryDisputeCard
      id={disputeRoundId(round.message.id)}
      fold={!waiting}
      dispute={round.dispute}
      concerns={payloadConcerns(round.message.payload)}
      answers={round.answers}
      closedBy={round.closedBy}
      unfinished={disputeUnfinished(round)}
      settled={disputeComplete(round)}
      onSettle={onSettle && waiting ? (picks) => onSettle(round.message.id, picks) : undefined}
      onPicksChange={onPicksChange}
      meta={
        <span data-hint={formatDateTime(new Date(round.message.created_at))}>
          {round.message.actor_label ? `${round.message.actor_label} · ` : null}
          {formatRelative(round.message.created_at)}
        </span>
      }
    />
  );
}
